import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import {
  createEmailTemplate,
  getEmailTemplateById,
  updateEmailTemplate,
  clearCurrentTemplate,
  previewEmailTemplate,
} from '../../redux/slices/emailTemplateSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import alertify from 'alertifyjs';

/**
 * EmailTemplateEditorPage - Admin page for creating/editing email templates
 *
 * Features:
 * - Form for key, title, subject, HTML content
 * - HTML content editor (textarea for now, can be upgraded to rich text editor)
 * - Live HTML preview
 * - isActive toggle
 * - Save/Update functionality
 * - Permission checks
 */
const EmailTemplateEditorPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const isEditMode = !!id;

  const { currentTemplate, loading, error } = useSelector(
    (state) => state.emailTemplates
  );

  const hasViewPermission = useHasPermission('admin.email-templates.view');
  const hasCreatePermission = useHasPermission('admin.email-templates.create');
  const hasEditPermission = useHasPermission('admin.email-templates.edit');

  const [templateState, setTemplateState] = useState({
    key: '',
    title: '',
    subject: '',
    htmlContent: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: '', html: '' });
  const [previewVariables, setPreviewVariables] = useState('{}');

  useEffect(() => {
    if (isEditMode && hasViewPermission) {
      dispatch(getEmailTemplateById(id));
    } else if (!isEditMode) {
      dispatch(clearCurrentTemplate());
    }
  }, [id, isEditMode, dispatch, hasViewPermission]);

  useEffect(() => {
    if (isEditMode && currentTemplate && currentTemplate._id === id) {
      setTemplateState({
        key: currentTemplate.key,
        title: currentTemplate.title,
        subject: currentTemplate.subject,
        htmlContent: currentTemplate.htmlContent,
        isActive: currentTemplate.isActive,
      });
    }
  }, [isEditMode, currentTemplate, id]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    // Special handling for key field: lowercase and trim
    if (name === 'key') {
      // Convert to lowercase and remove spaces
      const processedValue = value.toLowerCase().trim().replace(/\s+/g, '_');
      // Only allow lowercase letters, numbers, and underscores
      const sanitizedValue = processedValue.replace(/[^a-z0-9_]/g, '');
      setTemplateState((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setTemplateState((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSave = async () => {
    if (!hasCreatePermission && !hasEditPermission) {
      alertify.error('Bu işlem için yetkiniz yok');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await dispatch(
          updateEmailTemplate({ id, templateData: templateState })
        ).unwrap();
        alertify.success('Email şablonu başarıyla güncellendi');
      } else {
        await dispatch(createEmailTemplate(templateState)).unwrap();
        alertify.success('Email şablonu başarıyla oluşturuldu');
      }
      setSnackbar({
        open: true,
        message: isEditMode
          ? 'Email şablonu başarıyla güncellendi'
          : 'Email şablonu başarıyla oluşturuldu',
        severity: 'success',
      });
      setTimeout(() => {
        history.push('/admin/email-templates');
      }, 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Şablon kaydedilemedi',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      let variables = {};
      try {
        variables = JSON.parse(previewVariables || '{}');
      } catch (e) {
        alertify.warning('Değişkenler JSON formatında olmalıdır');
        return;
      }

      if (isEditMode && id) {
        const response = await dispatch(
          previewEmailTemplate({ id, variables })
        ).unwrap();
        setPreviewContent(response);
      } else {
        // For new templates, render manually
        let renderedHtml = templateState.htmlContent;
        let renderedSubject = templateState.subject;

        Object.keys(variables).forEach(key => {
          const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          const value = variables[key] !== undefined && variables[key] !== null 
            ? String(variables[key]) 
            : '';
          renderedHtml = renderedHtml.replace(regex, value);
          renderedSubject = renderedSubject.replace(regex, value);
        });

        setPreviewContent({ subject: renderedSubject, html: renderedHtml });
      }
      setPreviewDialogOpen(true);
    } catch (err) {
      alertify.error(err.message || 'Önizleme oluşturulamadı');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!hasViewPermission || (isEditMode && !hasEditPermission) || (!isEditMode && !hasCreatePermission)) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
            <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {isEditMode ? 'Email Şablonu Düzenle' : 'Yeni Email Şablonu Oluştur'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEditMode
                  ? 'Mevcut email şablonunu düzenleyin'
                  : 'Yeni bir email şablonu oluşturun'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Önizle">
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handlePreview}
                disabled={loading || saving}
              >
                Önizle
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || saving || (isEditMode && !hasEditPermission) || (!isEditMode && !hasCreatePermission)}
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            label="Anahtar (Key)"
            name="key"
            value={templateState.key}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isEditMode}
            error={!isEditMode && templateState.key.length > 0 && !/^[a-z0-9_]+$/.test(templateState.key)}
            helperText={
              isEditMode 
                ? "Anahtar düzenlenemez" 
                : templateState.key.length > 0 && !/^[a-z0-9_]+$/.test(templateState.key)
                  ? "Sadece küçük harf, rakam ve alt çizgi (_) kullanılabilir"
                  : "Benzersiz bir anahtar girin (örn: password_reset). Otomatik olarak küçük harfe çevrilir."
            }
          />
          <TextField
            label="Başlık"
            name="title"
            value={templateState.title}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Konu (Subject)"
            name="subject"
            value={templateState.subject}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            helperText="Dinamik alanlar için {{key}} kullanabilirsiniz (örn: Merhaba {{firstName}})"
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            HTML İçeriği
          </Typography>
          <TextField
            name="htmlContent"
            value={templateState.htmlContent}
            onChange={handleChange}
            fullWidth
            multiline
            rows={15}
            margin="normal"
            required
            helperText="Dinamik alanlar için {{key}} kullanabilirsiniz (örn: Merhaba {{firstName}})"
          />

          <FormControlLabel
            control={
              <Switch
                checked={templateState.isActive}
                onChange={handleChange}
                name="isActive"
                color="primary"
              />
            }
            label="Aktif"
            sx={{ mt: 2 }}
          />
        </Paper>

        {/* HTML Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Email Şablonu Önizleme: {previewContent.subject}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" sx={{ mb: 1 }}>Test Değişkenleri (JSON)</Typography>
            <TextField
              label="Değişkenler"
              multiline
              rows={4}
              fullWidth
              value={previewVariables}
              onChange={(e) => setPreviewVariables(e.target.value)}
              helperText="Örn: { 'firstName': 'Ali', 'resetUrl': 'https://...' }"
              sx={{ mb: 2 }}
            />
            <Button
              variant="outlined"
              onClick={handlePreview}
              sx={{ mb: 2 }}
            >
              Önizlemeyi Yenile
            </Button>
            <iframe
              srcDoc={previewContent.html}
              title="Email Preview"
              style={{ width: '100%', height: '500px', border: 'none' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Kapat</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EmailTemplateEditorPage;

